import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  alpha,
  useTheme,
  Icon,
  Paper,
} from "@mui/material";
import { IChartOfAccount } from "../../../../accounting.interface";

interface MergeAccountDialogProps {
  open: boolean;
  onClose: () => void;
  sourceAccount: IChartOfAccount;
  availableAccounts: IChartOfAccount[];
  onMerge: (mergeData: {
    sourceAccountId: string;
    targetAccountId: string;
    totalOpeningBalance: number;
    totalCurrentBalance: number;
  }) => void;
  isLoading?: boolean;
}

// Visual Account Node Component
const AccountNodeCard: React.FC<{
  account: IChartOfAccount | null;
  title: string;
  subtitle: string;
  isPlaceholder?: boolean;
  color?: string;
}> = ({
  account,
  title,
  subtitle,
  isPlaceholder = false,
  color = "primary",
}) => {
  const theme = useTheme();

  if (!account && isPlaceholder) {
    return (
      <Paper
        sx={{
          p: 3,
          minHeight: 140,
          border: `2px dashed ${alpha(theme.palette.divider, 0.4)}`,
          bgcolor: alpha(theme.palette.background.paper, 0.5),
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 2,
        }}
      >
        <Icon
          sx={{
            fontSize: 48,
            color: alpha(theme.palette.text.secondary, 0.3),
            mb: 1,
          }}
        >
          help_outline
        </Icon>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", textAlign: "center" }}
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", textAlign: "center" }}
        >
          {subtitle}
        </Typography>
      </Paper>
    );
  }

  if (!account) return null;

  const getAccountTypeColor = (type: string) => {
    switch (type) {
      case "Asset":
        return "#10B981";
      case "Liability":
        return "#EF4444";
      case "Equity":
        return "#3B82F6";
      case "Revenue":
        return "#8B5CF6";
      case "Expense":
        return "#F59E0B";
      default:
        return theme.palette.primary.main;
    }
  };

  const accountColor = getAccountTypeColor(account.accountType);

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `2px solid ${accountColor}`,
        bgcolor: alpha(accountColor, 0.05),
        position: "relative",
        minHeight: 140,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            bgcolor: accountColor,
          }}
        />
        <Typography
          variant="caption"
          sx={{ color: accountColor, fontWeight: 600 }}
        >
          {title}
        </Typography>
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.2 }}>
        {account.accountName}
      </Typography>

      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Code: {account.accountCode} • {account.accountType}
      </Typography>

      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Opening Balance
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "primary.main" }}
            >
              {account.currency}{" "}
              {(account.openingBalance || 0).toLocaleString()}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Current Balance
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "success.main" }}
            >
              {account.currency}{" "}
              {(account.currentBalance || 0).toLocaleString()}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Typography
        variant="caption"
        sx={{ color: "text.secondary", mt: 1, display: "block" }}
      >
        {subtitle}
      </Typography>
    </Paper>
  );
};

export const MergeAccountDialog: React.FC<MergeAccountDialogProps> = ({
  open,
  onClose,
  sourceAccount,
  availableAccounts,
  onMerge,
  isLoading = false,
}) => {
  const theme = useTheme();
  const [targetAccountId, setTargetAccountId] = useState("");

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTargetAccountId("");
    }
  }, [open]);

  // Filter accounts that can be merged with (same type, same level, different account)
  const mergeableAccounts = availableAccounts.filter(
    (acc) =>
      acc.id !== sourceAccount.id &&
      acc.accountType === sourceAccount.accountType &&
      acc.level === sourceAccount.level &&
      acc.openingBalance !== undefined &&
      acc.isActive !== false // Exclude inactive accounts
  );

  const targetAccount = mergeableAccounts.find(
    (acc) => acc.id === targetAccountId
  );
  const totalOpeningBalance = targetAccount
    ? (sourceAccount.openingBalance || 0) + (targetAccount.openingBalance || 0)
    : 0;
  const totalCurrentBalance = targetAccount
    ? (sourceAccount.currentBalance || 0) + (targetAccount.currentBalance || 0)
    : 0;

  const handleMerge = () => {
    if (!targetAccountId) return;

    onMerge({
      sourceAccountId: sourceAccount.id,
      targetAccountId,
      totalOpeningBalance,
      totalCurrentBalance,
    });
  };

  const isValid = !!targetAccountId;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: alpha(theme.palette.info.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Icon sx={{ color: "info.main", fontSize: 28 }}>merge</Icon>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "info.main" }}
            >
              Merge Accounts
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              Combine balances from source account into target account
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        {/* Visual Flow Diagram */}
        <Box sx={{ my: 4 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Source Account Node */}
            <Grid item xs={12} md={5}>
              <AccountNodeCard
                account={sourceAccount}
                title="SOURCE ACCOUNT"
                subtitle="Will be marked inactive after merge"
              />
            </Grid>

            {/* Flow Arrow */}
            <Grid item xs={12} md={2}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: "center",
                  justifyContent: "center",
                  py: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexDirection: { xs: "column", md: "row" },
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 32,
                      color: "warning.main",
                      transform: { md: "rotate(0deg)", xs: "rotate(90deg)" },
                    }}
                  >
                    trending_flat
                  </Icon>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "warning.main",
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  >
                    MERGE INTO
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Target Account Node or Placeholder */}
            <Grid item xs={12} md={5}>
              <AccountNodeCard
                account={targetAccount || null}
                title="Select Target Account"
                subtitle="Will receive all merged balances"
                isPlaceholder={!targetAccount}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Target Account Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Choose Target Account
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Select account to merge into</InputLabel>
            <Select
              value={targetAccountId}
              onChange={(e) => setTargetAccountId(e.target.value)}
              label="Select account to merge into"
              disabled={isLoading}
            >
              {mergeableAccounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {account.accountName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {account.accountCode} • {account.accountType}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "primary.main" }}
                      >
                        Opening: {account.currency}{" "}
                        {(account.openingBalance || 0).toLocaleString()}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "success.main" }}
                      >
                        Current: {account.currency}{" "}
                        {(account.currentBalance || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Merge Results Preview */}
        {targetAccount && (
          <Paper
            sx={{
              p: 3,
              bgcolor: alpha(theme.palette.success.main, 0.04),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: "success.main",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ fontSize: 20 }}>calculate</Icon>
              Merged Account Result
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
                  >
                    Total Opening Balance
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "primary.main" }}
                  >
                    {sourceAccount.currency}{" "}
                    {totalOpeningBalance.toLocaleString()}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {(sourceAccount.openingBalance || 0).toLocaleString()} +{" "}
                    {(targetAccount.openingBalance || 0).toLocaleString()}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.05),
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 1, color: "success.main" }}
                  >
                    Total Current Balance
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "success.main" }}
                  >
                    {sourceAccount.currency}{" "}
                    {totalCurrentBalance.toLocaleString()}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {(sourceAccount.currentBalance || 0).toLocaleString()} +{" "}
                    {(targetAccount.currentBalance || 0).toLocaleString()}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>{sourceAccount.accountName}</strong> will be marked as{" "}
                <strong>INACTIVE</strong> and all its balances will be
                transferred to <strong>{targetAccount.accountName}</strong>.
              </Typography>
            </Alert>
          </Paper>
        )}

        {mergeableAccounts.length === 0 && (
          <Alert severity="warning">
            No accounts available for merging. Accounts can only be merged with
            other accounts of the same type and level.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 3 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleMerge}
          disabled={!isValid || isLoading}
          startIcon={
            isLoading ? <Icon>hourglass_empty</Icon> : <Icon>merge</Icon>
          }
        >
          {isLoading ? "Merging..." : "Merge Accounts"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
